import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBdFormat } from '../../../hooks/useBasicDesign.js';
import type { BdArtifact } from '../../../lib/api/basic-design.js';

export interface BdFormatHintProps {
  /** 書き方を開く節 */
  artifact: BdArtifact;
  /** 見出しに出す節の名前 */
  label: string;
}

/**
 * 節の書き方（API が返すフォーマット文書）を開閉して読む。開くまで取りに行かない。
 *
 * 書き方の正は API 側のフォーマット文書で、MCP の `get_bd_format` も同じものを返す。
 * 画面に写さないので、文書が変わってもこの画面は直さなくてよい。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の節の書き方（フォーマット文書）を開いたときだけ読み込んで表示する
 * @graph-connects useBdFormat [calls] フォーマット文書の取得
 */
export function BdFormatHint({ artifact, label }: BdFormatHintProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const format = useBdFormat(artifact, open);

  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
      <button
        type="button"
        className="flex w-full items-center justify-between px-4 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v);
        }}
      >
        <span>
          {open ? t('bd.format.hide', { title: label }) : t('bd.format.show', { title: label })}
        </span>
        <span aria-hidden="true">{open ? '−' : '+'}</span>
      </button>
      {open ? (
        <div className="border-t border-[var(--color-border)] px-4 py-3">
          {format.error !== null ? (
            <p className="text-sm text-[var(--color-error-text)]">{t('bd.format.failed')}</p>
          ) : format.data === undefined ? (
            <p className="text-sm text-[var(--color-text-muted)]">{t('bd.loading')}</p>
          ) : (
            <pre className="max-h-96 overflow-auto whitespace-pre-wrap font-mono text-xs leading-relaxed text-[var(--color-text-secondary)]">
              {format.data.markdown}
            </pre>
          )}
        </div>
      ) : null}
    </div>
  );
}
